import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import ConfirmDialog from "components/Dialogs/ConfirmDialog";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import { getApplications, changeApplicationStatus, deleteApplication } from "data/controllers/applicationsController";

const applicationColumns = [
  { Header: "candidate", accessor: "candidate", align: "left" },
  { Header: "location", accessor: "location", align: "center" },
  { Header: "applied on", accessor: "appliedOn", align: "center" },
  { Header: "status", accessor: "status", align: "center" },
  { Header: "action", accessor: "action", align: "center" },
];

function formatStatus(status) {
  const statusMap = {
    "applied": "Applied",
    "shortlisted": "Shortlisted",
    "rejected": "Rejected"
  };
  return statusMap[status] || status;
}

function Applications() {
  const location = useLocation();
  const navigate = useNavigate();
  const [applicationRows, setApplicationRows] = useState([]);
  const [applicationsData, setApplicationsData] = useState([]);
  const [applicationPage, setApplicationPage] = useState(1);
  const [applicationTotal, setApplicationTotal] = useState(0);
  const [applicationLimit, setApplicationLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("newToOld");
  const [statusFilter, setStatusFilter] = useState("all");

  // Get status filter from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const statusParam = searchParams.get("status");
    if (statusParam && ["applied", "shortlisted", "rejected"].includes(statusParam)) {
      setStatusFilter(statusParam);
    } else {
      setStatusFilter("all");
    }
  }, [location.search]);

  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // View candidate dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateToDelete, setCandidateToDelete] = useState(null);

  const sortOptions = [
    { label: "New to Old", value: "newToOld" },
    { label: "Old to New", value: "oldToNew" },
    { label: "Alphabetically", value: "alphabetically" },
  ];

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      setDebouncedSearchTerm(searchTerm);
      setApplicationPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setApplicationPage(1);
  };

  const handleSortChange = (event) => {
    setSortOption(event.target.value);
    setApplicationPage(1);
  };

  const handleStatusFilterChange = (event) => {
    const selectedStatus = event.target.value;
    setStatusFilter(selectedStatus);
    setApplicationPage(1);

    // Update URL without causing a page reload
    const newSearchParams = new URLSearchParams();
    if (selectedStatus !== "all") {
      newSearchParams.set("status", selectedStatus);
    }
    const newUrl = `/applications${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ""}`;
    navigate(newUrl, { replace: true });
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await changeApplicationStatus(applicationId, newStatus);
      setApplicationsData((prevApplications) =>
        prevApplications.map((app) => (app._id === applicationId ? { ...app, status: newStatus } : app))
      );
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to update application status");
      setErrorDialogOpen(true);
    }
  };

  const handleViewClick = (application) => {
    setSelectedCandidate(application);
    setViewDialogOpen(true);
  };

  const handleDownloadResume = (resumeUrl, candidateName) => {
    if (!resumeUrl) {
      setErrorDialogMessage("No resume available for this candidate.");
      setErrorDialogOpen(true);
      return;
    }

    const link = document.createElement("a");
    link.href = resumeUrl;
    link.download = `${candidateName.replace(/\s+/g, "_")}_Resume.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    async function fetchApplications() {
      const { applications, pagination } = await getApplications(
        applicationPage, 
        applicationLimit, 
        debouncedSearchTerm, 
        sortOption,
        statusFilter
      );
      setApplicationsData(applications);
      setApplicationTotal(pagination?.total || 0);
      setTotalPages(Math.ceil((pagination?.total || 0) / applicationLimit));
    }
    fetchApplications();
  }, [applicationPage, applicationLimit, debouncedSearchTerm, sortOption, statusFilter]);

  useEffect(() => {
    const formattedApplications = applicationsData.map((application) => ({
      candidate: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDAvatar src={application.profilePicture} name={application.name} size="sm" />
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="button" fontWeight="medium">
              {application.name}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text"
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                maxWidth: "200px",
                mt: 0.5,
              }}
            >
              {application.jobTitle}
            </MDTypography>
          </MDBox>
        </MDBox>
      ),
      location: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {application.location}
        </MDTypography>
      ),
      appliedOn: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {application.appliedAt
            ? new Date(application.appliedAt).toLocaleString(undefined, {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
            : ""}
        </MDTypography>
      ),
      status: (
        <select
          value={application.status || "applied"}
          onChange={(e) => handleStatusChange(application._id, e.target.value)}
          style={{
            padding: "4px 8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontSize: "0.875rem",
            minWidth: "120px",
            width: "120px",
            backgroundColor:
              {
                "applied": "#fff3cd",
                "shortlisted": "#d4edda",
                "rejected": "#f8d7da",
              }[application.status] || "#fff",
            color:
              {
                "applied": "#856404",
                "shortlisted": "#155724",
                "rejected": "#721c24",
              }[application.status] || "#000",
          }}
        >
          <option value="applied">Applied</option>
          <option value="shortlisted">Shortlisted</option>
          <option value="rejected">Rejected</option>
        </select>
      ),
      action: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <IconButton size="small" color="info" onClick={() => handleDownloadResume(application.resume, application.name)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="info" onClick={() => handleViewClick(application)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteClick(application._id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </MDBox>
      ),
      // Add sorting fields for client-side sorting
      candidateName: application.name || "Unknown",
      appliedAtDate: application.appliedAt ? new Date(application.appliedAt) : new Date(0),
    }));

    setApplicationRows(formattedApplications);
  }, [applicationsData]);

  const handleDeleteClick = (applicationId) => {
    const candidate = applicationsData.find(app => app._id === applicationId);
    setApplicationToDelete(applicationId);
    setCandidateToDelete(candidate);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteApplication = async () => {
    if (!applicationToDelete) return;
    setConfirmDialogOpen(false);
    setIsDeleting(true);
    try {
      await deleteApplication(applicationToDelete);
      setApplicationsData((prevApplications) => 
        prevApplications.filter((app) => app._id !== applicationToDelete)
      );
    } catch (error) {
      setErrorDialogMessage(error.message || "Failed to delete application");
      setErrorDialogOpen(true);
    } finally {
      setIsDeleting(false);
      setApplicationToDelete(null);
      setCandidateToDelete(null);
    }
  };

  const handlePageChange = (_, value) => {
    setApplicationPage(value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Applications Table
                </MDTypography>
              </MDBox>
              <MDBox px={2} pt={2}>
                {/* Search, Filter and Sort Controls - Single line on desktop, stacked on mobile */}
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={2}
                  mb={2}
                  sx={{
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    "& > *": {
                      width: { xs: "100%", md: "auto" }
                    }
                  }}
                >
                  {/* Search Bar */}
                  <MDBox
                    display="flex"
                    alignItems="center"
                    sx={{
                      flex: { xs: "none", md: 1 },
                      width: { xs: "100%", md: "auto" },
                      minWidth: 0
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search by candidate name, job title, or location"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleKeyDown}
                      style={{
                        flex: 1,
                        padding: "10px",
                        fontSize: "14px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        minWidth: 0, // Allow shrinking
                      }}
                    />
                    {searchTerm && (
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "block" }
                      }}
                    >
                      Filter:
                    </MDTypography>
                    <FormControl size="small" sx={{ flex: 1, minWidth: "120px" }}>
                      <Select
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        displayEmpty
                        sx={{
                          height: "36px",
                          fontSize: "14px",
                          "& .MuiSelect-select": {
                            padding: "8px 24px 8px 12px",
                          },
                        }}
                      >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="applied">Applied</MenuItem>
                        <MenuItem value="shortlisted">Shortlisted</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </MDBox>

                  <MDBox display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                    <MDTypography
                      variant="caption"
                      fontWeight="medium"
                      sx={{
                        whiteSpace: "nowrap",
                        display: { xs: "none", sm: "block" }
                      }}
                    >
                      Sort by:
                    </MDTypography>
                    <Select
                      value={sortOption}
                      onChange={handleSortChange}
                      sx={{
                        height: "36px",
                        fontSize: "14px",
                        flex: 1,
                        minWidth: "140px",
                        "& .MuiSelect-select": {
                          padding: "8px 24px 8px 12px",
                        },
                      }}
                    >
                      {sortOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns: applicationColumns, rows: applicationRows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={2}
                mt={2}
                px={3}
              >
                <Pagination
                  count={totalPages}
                  page={applicationPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton={window.innerWidth > 768}
                  showLastButton={window.innerWidth > 768}
                  siblingCount={window.innerWidth > 768 ? 1 : 0}
                  boundaryCount={window.innerWidth > 768 ? 1 : 1}
                  sx={{
                    ml: { xs: 0, sm: 2 },
                    "& .MuiPaginationItem-root": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      minWidth: { xs: "28px", sm: "32px" },
                      height: { xs: "28px", sm: "32px" },
                    }
                  }}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Delete Application"
        description={`Are you sure you want to delete the application from ${candidateToDelete?.name || 'this candidate'}? This action cannot be undone.`}
        onClose={() => {
          setConfirmDialogOpen(false);
          setCandidateToDelete(null);
        }}
        onConfirm={confirmDeleteApplication}
        confirmButtonText="Delete"
        confirmButtonColor="error"
      />

      {/* View Candidate Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3, position: "relative" }}>
          <MDBox display="flex" alignItems="center" gap={2}>
            <MDAvatar
              src={selectedCandidate?.profilePicture}
              name={selectedCandidate?.name}
              size="lg"
            />
            <MDBox flex={1}>
              <MDTypography variant="h5" fontWeight="medium" color="dark">
                {selectedCandidate?.name}
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
                Applied for: {selectedCandidate?.jobTitle}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                at {selectedCandidate?.companyName}
              </MDTypography>
            </MDBox>
          </MDBox>
          {/* Close Icon */}
          <IconButton
            onClick={() => setViewDialogOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5"
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 3, px: 3 }}>
          <Grid container spacing={3}>
            {/* Experience */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <WorkIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Experience
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedCandidate?.experience}
              </MDTypography>
            </Grid>

            {/* Contact Number */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <PhoneIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Contact Number
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedCandidate?.phone}
              </MDTypography>
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <EmailIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Email
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedCandidate?.email}
              </MDTypography>
            </Grid>

            {/* Address */}
            <Grid item xs={12} md={6}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <HomeIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Address
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedCandidate?.address}
              </MDTypography>
            </Grid>

            {/* Introduction */}
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Introduction
                </MDTypography>
              </MDBox>
              <MDTypography variant="body2" color="text" sx={{ ml: 3 }}>
                {selectedCandidate?.introduction}
              </MDTypography>
            </Grid>

            {/* Skills */}
            <Grid item xs={12}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <WorkIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                <MDTypography variant="h6" fontWeight="medium">
                  Skills
                </MDTypography>
              </MDBox>
              <MDBox display="flex" flexWrap="wrap" gap={1} sx={{ ml: 3 }}>
                {selectedCandidate?.skills?.map((skill, index) => (
                  <MDBox
                    key={index}
                    sx={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      padding: "4px 12px",
                      borderRadius: "16px",
                      fontSize: "0.875rem",
                      fontWeight: "medium"
                    }}
                  >
                    {skill}
                  </MDBox>
                ))}
              </MDBox>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{errorDialogMessage}</MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Applications;
